import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { DirectoryFeedProvider } from '@/modules/directory-feed/components/directory-feed-provider'
import { DirectoryInvalidation } from '@/modules/file-manager/components/directory-invalidation'
import { ShortcutsProvider } from '@/modules/shortcuts/components/shortcuts-provider'
import {
	clientSessionQueryOptions,
	warmInstanceBranding
} from '@/query/query-options'
import { rememberOrganization } from '@/routes/-lib/last-organization'

export const Route = createFileRoute('/(private)')({
	ssr: false,
	beforeLoad: async ({ context, location }) => {
		// The session gate. `fetchQuery` rather than `ensureQueryData` because
		// this is the point where the session may not be in the cache yet, and
		// guards cannot suspend; everything below reads the cache it warms,
		// including the role check on `/admin`.
		const session = await context.queryClient.fetchQuery(
			clientSessionQueryOptions()
		)

		// Decide and stop; do not return data. Route context carries nothing
		// session-derived — components re-read the same queryOptions, which
		// stay subscribed while a context snapshot goes stale.
		//
		// Only an EMPTY session lands here. Backend and network failures reach
		// the route error boundary instead, so a flaky connection is never
		// reported to the user as "you are signed out".
		if (session.sessionType !== 'authenticated') {
			throw redirect({
				to: '/login',
				search: { returnPath: location.href }
			})
		}

		// Here and not only on `/o/$slug/login`, because not every way in goes
		// through that page: an invitation signs the person in with the
		// password they have just chosen, so their first `/login` would ask
		// for a name they never typed. The organization the session is in is
		// the one to come back to, whichever door it came through.
		rememberOrganization(session.organization.slug)
	},
	// The sidebar draws the reseller's name and mark, so they are read before
	// it renders and never pop in after the first paint. Never fails the page.
	loader: ({ context }) => warmInstanceBranding(context.queryClient),
	component: ClientPrivateLayout
})

function ClientPrivateLayout() {
	// The directory channel is opened here and nowhere below, because it
	// belongs to the session rather than to a page: a subscription owned by a
	// folder route would close and reopen on every click, and the cost of that
	// is a handshake and two token mints each time. Wrapping the shell rather
	// than sitting inside it also means the connection survives every
	// navigation this layout renders.
	//
	// No chrome here: the sidebar is `(shell)/route.tsx`'s, so a page that
	// takes the whole window — `view/$nodeId`, a file opened in a tab of its
	// own — sits beside that group and keeps the gate and the channel.
	return (
		<DirectoryFeedProvider>
			<DirectoryInvalidation />
			<ShortcutsProvider>
				<Outlet />
			</ShortcutsProvider>
		</DirectoryFeedProvider>
	)
}
